<template>
  <div class="bulk-upload-dialog-wrapper">
    <v-dialog
      :value="show"
      fullscreen
      persistent
      transition="dialog-bottom-transition"
      @input="$emit('update:show', $event)"
    >
      <v-card v-if="show">
        <DrawerToolbar title="Upload a Bulk Claims File" admin @close="handleClose" />

        <div
          class="drawer-container"
          :class="step === steps.upload_csv ? 'drawer-container--narrow' : 'drawer-container--wide'"
        >
          <div
            v-if="step !== steps.submission_result"
            class="d-flex"
            :class="step !== steps.upload_csv ? 'justify-space-between' : 'justify-end'"
          >
            <Button
              v-if="step !== steps.upload_csv"
              icon-left="mdi-chevron-left"
              @click="handlePrevious"
              outlined
            >
              Back
            </Button>
            <Button
              v-if="step !== steps.edit_bulk_submission"
              class="d-flex align-self-end"
              :disabled="!nextEnabled"
              icon-right="mdi-chevron-right"
              :loading="nextLoading"
              @click="handleNext"
            >
              Next
            </Button>
          </div>
          <UploadCSVStep
            v-if="step === steps.upload_csv"
            ref="uploadStep"
            :value="selectedFormId"
            @input="selectedFormId = $event"
            :form-options="formOptions"
            :gettingFormQuestions="gettingFormQuestions"
            :search-loading="searchLoading"
            :columns="columns"
            :file="file"
            @file-selected="onFileSelected"
            @search="$emit('search-forms', $event)"
          />
          <MapColumnsStep
            v-if="step === steps.map_columns"
            :rows="rows"
            :columns="columns"
            @mapped-csv-columns-updated="onMappedRowsUpdated"
          />
          <EditBulkSubmission
            v-if="step === steps.edit_bulk_submission"
            :submit-loading="submitLoading"
            :mappedRows="mappedRowsWithUuids"
            :columns="columns"
            :mappedRowsErrors="mappedRowsErrors"
            :onDeleteRow="handleDeleteRow"
            :onRevalidateAll="handleRevalidateAll"
            @submit-bulk-claims="handleSubmitBulkClaims"
          >
            <template #header>
              <div class="text-center">
                <Typography variant="h-4">Review and Validate Claims</Typography>
                <Typography variant="subtitle-1">
                  Submit valid claims now or fix errors first
                </Typography>
              </div>
            </template>
          </EditBulkSubmission>

          <BulkSubmissionResult
            v-if="step === steps.submission_result"
            :bulk-claim-submission-id="bulkClaimSubmissionId"
            :success-count="submissionSuccessCount"
            :failed-count="submissionFailedCount"
            :invalid-count="submissionInvalidCount"
            @done="handleClose"
            @fix-invalid-claims="handleFixInvalidClaims"
          />
        </div>
      </v-card>
    </v-dialog>
    <Modal
      :value="showExitConfirmModal"
      @input="showExitConfirmModal = $event"
      :show-close-button="false"
      :onClose="onStayOnPage"
      data-testid="bulk-upload-exit-confirm-modal"
    >
      <template #header>
        <Typography variant="h-5">Are you sure you want to exit?</Typography>
      </template>
      <Typography variant="body-1" class="text-left black--text pt-4">
        If you exit now, all your work will be lost.
      </Typography>
      <div class="d-flex justify-end pt-4">
        <Button text @click="onStayOnPage">Stay on page</Button>
        <Button text @click="onConfirmExit">Exit</Button>
      </div>
    </Modal>
  </div>
</template>

<script>
import DrawerToolbar from "@/components/generic/DrawerToolbar.vue";
import UserService from "@/services/UserService.js";
import FormService from "@/services/FormService.js";
import UploadCSVStep from "./UploadCSVStep.vue";
import MapColumnsStep from "./MapColumnsStep.vue";
import BulkSubmissionResult from "./BulkSubmissionResult.vue";
import Button from "@/components/generic/Button.vue";
import Modal from "@/components/generic/Modal.vue";
import EditBulkSubmission from "../EditBulkSubmission/EditBulkSubmission.vue";
import { v4 as uuidv4 } from "uuid";
import Typography from "@/components/generic/Typography.vue";
import getColumnsFromForm from "../mixins/getColumnsFromForm.js";
const STEPS = {
  upload_csv: "Upload CSV",
  map_columns: "Map Columns",
  edit_bulk_submission: "Edit Bulk Submission",
  submission_result: "Submission Result",
};

export default {
  name: "BulkUploadDialog",
  mixins: [getColumnsFromForm],
  components: {
    DrawerToolbar,
    UploadCSVStep,
    MapColumnsStep,
    Button,
    Modal,
    EditBulkSubmission,
    Typography,
    BulkSubmissionResult,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    forms: {
      type: Array,
      default: () => [],
    },
    searchLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      step: STEPS.upload_csv,
      file: null,
      nextLoading: false,
      steps: STEPS,
      selectedFormId: null,
      rows: [],
      mappedRows: [],
      mappedRowsErrors: [],
      mappedRowsWithUuids: [],
      bulkClaimSubmissionId: null,
      submissionSuccessCount: 0,
      submissionFailedCount: 0,
      submissionInvalidCount: 0,
      submitLoading: false,
      showExitConfirmModal: false,
    };
  },
  computed: {
    formOptions() {
      return this.forms.map((form) => ({ text: form.name, value: form.formId }));
    },
    selectedForm() {
      return this.forms.find((form) => form.formId === this.selectedFormId);
    },
    nextEnabled() {
      if (this.step === this.steps.upload_csv) {
        return !!this.file;
      } else if (this.step === this.steps.map_columns) {
        return this.mappedRows.length > 0;
      }
      return true;
    },
    needsExitConfirmation() {
      if (this.step === this.steps.submission_result) {
        return false;
      }
      return this.file != null || this.step !== this.steps.upload_csv;
    },
  },
  methods: {
    handleClose() {
      if (this.needsExitConfirmation) {
        this.showExitConfirmModal = true;
        return;
      }
      this.performClose();
    },
    onStayOnPage() {
      this.showExitConfirmModal = false;
    },
    onConfirmExit() {
      this.performClose();
    },
    performClose() {
      this.showExitConfirmModal = false;
      this.resetState();
      this.$emit("update:show", false);
    },
    resetState() {
      this.step = STEPS.upload_csv;
      this.file = null;
      this.selectedFormId = null;
      this.rows = [];
      this.mappedRows = [];
      this.mappedRowsErrors = [];
      this.mappedRowsWithUuids = [];
      this.bulkClaimSubmissionId = null;
      this.submissionSuccessCount = 0;
      this.submissionFailedCount = 0;
      this.submissionInvalidCount = 0;
      this.submitLoading = false;
      this.showExitConfirmModal = false;
    },
    async onFileSelected(file) {
      this.file = file;
    },
    async convertCSVToJson() {
      if (!this.file) return;
      this.nextLoading = true;
      const response = await UserService.csvToJson(this.file);
      this.nextLoading = false;
      this.rows = response.result.rows;
    },
    async handleNext() {
      this.nextLoading = true;
      try {
        if (this.step === this.steps.upload_csv) {
          await this.convertCSVToJson();
          this.step = this.steps.map_columns;
        } else if (this.step === this.steps.map_columns) {
          // eslint-disable-next-line no-unused-vars
          this.mappedRowsWithUuids = this.mappedRows.map(({ __row, ...rest }) => ({
            ...rest,
            id: uuidv4(),
          }));

          const claims = this.mappedRowsWithUuids.map(({ id, email, ...claimData }) => ({
            id,
            email: email == null ? "" : String(email),
            ...claimData,
          }));

          await this.handleRevalidateAll(claims);
          this.step = this.steps.edit_bulk_submission;
        }
      } finally {
        this.nextLoading = false;
      }
    },
    async handlePrevious() {
      if (this.step === this.steps.map_columns) {
        this.step = this.steps.upload_csv;
      } else if (this.step === this.steps.edit_bulk_submission) {
        this.step = this.steps.map_columns;
      }
    },
    onMappedRowsUpdated(mappedRows) {
      this.mappedRows = mappedRows;
    },
    async handleRevalidateAll(claims = []) {
      if (!this.selectedFormId) return;

      this.mappedRowsWithUuids = claims;

      const response = await FormService.validateBulkClaimSubmission(this.selectedFormId, claims);
      this.mappedRowsErrors = response || [];
    },
    async handleSubmitBulkClaims({ claims }) {
      if (!this.selectedFormId) return;
      this.submitLoading = true;
      try {
        const response = await FormService.postBulkClaimSubmission(
          this.selectedFormId,
          claims,
          this.bulkClaimSubmissionId
        );
        const nextId = response?.result?.bulkClaimSubmission?.id;
        const successCount = response?.result?.successCount ?? 0;
        const failedCount = response?.result?.failedCount ?? 0;
        const bulkClaims = response?.result?.bulkClaimSubmission?.BulkClaims;
        if (nextId !== undefined && nextId !== null) {
          this.bulkClaimSubmissionId = nextId;
        }
        this.submissionSuccessCount = successCount;
        this.submissionFailedCount = failedCount;
        this.submissionInvalidCount = Array.isArray(bulkClaims)
          ? bulkClaims.filter((claim) => claim.status === "Invalid").length
          : 0;
        this.step = this.steps.submission_result;
        this.$emit("bulk-upload-submitted");
      } finally {
        this.submitLoading = false;
      }
    },
    handleFixInvalidClaims() {
      this.$emit("open-edit-submission", {
        bulkClaimSubmissionId: this.bulkClaimSubmissionId,
        formId: this.selectedFormId,
      });
    },
    handleDeleteRow(rowId) {
      this.mappedRowsWithUuids = this.mappedRowsWithUuids.filter((row) => row.id !== rowId);
      // TODO: after submitting and re-editing, handle delete row deletes from the bulk submission instead
    },
  },
  watch: {
    show(val) {
      if (!val) {
        this.resetState();
      }
    },
    forms: {
      immediate: true,
      async handler(newForms) {
        if (newForms?.length && !this.selectedFormId) {
          this.selectedFormId = newForms[0].formId;
        }
      },
    },
    selectedFormId: {
      immediate: true,
      async handler(newFormId) {
        if (newFormId) {
          await this.getColumnsFromForm(newFormId);
        }
      },
    },
  },
};
</script>

<style scoped>
.bulk-upload-dialog-wrapper {
  display: contents;
}

.drawer-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 75px;
  padding-bottom: 24px;
}

.drawer-container--narrow {
  max-width: 684px;
  margin-left: auto;
  margin-right: auto;
}

.drawer-container--wide {
  padding-left: 4rem;
  padding-right: 4rem;
  width: 100%;
}
</style>
